import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';
import { CalendarView } from '../components/CalendarView';
import { EventFicha } from '../components/EventFicha';
import { CreateEventDialog } from '../components/CreateEventDialog';
import { useCalendarSyncRealtimeEvents } from '../hooks/useCalendarSyncRealtimeEvents';
import { useCalendarEvents } from '../hooks/useCalendarEvents';
import { useDeleteCalendarEvent } from '../hooks/useDeleteCalendarEvent';
import { EXTERNAL_CALENDAR_EVENTS_KEYS } from '../hooks/queryKeys';
import { useOpenTask } from '@/features/tasks/public';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { Toolbar } from '@/shared/components/custom/toolbar';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { useToast } from '@/shared/hooks/useToast';
import type { CalendarEvent } from '../types';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';

export default function CalendarPage() {
  const { t } = useTranslation('calendar');
  const openTask = useOpenTask();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Shares the exact same query (same key) CalendarView subscribes to — no
  // extra network request, just another reader of the same cached list.
  const { events } = useCalendarEvents();

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  // Shown immediately on click/create, before the (forced) refetch below
  // lands — replaced by the live match from `events` as soon as it does, so
  // the ficha never shows data staler than "whatever was just refetched."
  const [selectedEventFallback, setSelectedEventFallback] = useState<CalendarEvent | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [defaultSlot, setDefaultSlot] = useState<{ start: Date; end: Date } | null>(null);
  const deleteEvent = useDeleteCalendarEvent();
  const {
    confirm,
    isOpen: confirmOpen,
    options: confirmOptions,
    handleConfirm,
    handleCancel,
  } = useConfirm();

  useCalendarSyncRealtimeEvents(orgId ?? '');

  const selectedEvent = selectedEventId
    ? (events.find((e) => e.id === selectedEventId) ?? selectedEventFallback)
    : null;

  function openFicha(event: CalendarEvent) {
    setSelectedEventId(event.id);
    setSelectedEventFallback(event);
    // Force a fresh fetch of the unified feed every time the ficha opens, so
    // it never shows data older than "as of right now" (e.g. a change made
    // by someone else, or from another tab, since the list was last fetched).
    if (orgId) {
      queryClient.invalidateQueries({ queryKey: EXTERNAL_CALENDAR_EVENTS_KEYS.root(orgId) });
    }
  }

  function closeFicha() {
    setSelectedEventId(null);
    setSelectedEventFallback(null);
  }

  function handleEventClick(event: CalendarEvent) {
    if (event.type === 'task') {
      openTask(event.id);
      return;
    }
    openFicha(event);
  }

  function handleCreateEvent() {
    setDefaultSlot(null);
    setIsCreateOpen(true);
  }

  function handleSlotSelect(slot: { start: Date; end: Date }) {
    setDefaultSlot(slot);
    setIsCreateOpen(true);
  }

  function handleCreated(event: CalendarEvent) {
    // Opens the ficha right away so the user can fill in location/modality/
    // attendees/description — matches the quick-create → ficha flow used by Tasks.
    openFicha(event);
  }

  async function handleDeleteEvent(event: CalendarEvent) {
    const { confirmed } = await confirm({
      title: t('eventDetail.deleteConfirmTitle'),
      description: t('eventDetail.deleteConfirmDescription'),
      variant: 'destructive',
    });
    if (!confirmed) return;

    try {
      await deleteEvent.mutateAsync(event.id);
      closeFicha();
      toast.success({ title: t('eventDetail.deleteToast.success') });
    } catch {
      // global handler shows error toast
    }
  }

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <div className="flex h-full flex-col gap-4">
        <Toolbar className="pt-0">
          <Toolbar.Info title={t('page.title')} description={t('page.subtitle')} />
          <Toolbar.Actions>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="default"
                  onClick={handleCreateEvent}
                  aria-label={t('createEvent.title')}
                  data-testid="calendar-page-create-event-button"
                >
                  <PlusIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('createEvent.title')}</TooltipContent>
            </Tooltip>
          </Toolbar.Actions>
        </Toolbar>

        <div className="min-h-0 flex-1">
          <CalendarView onEventClick={handleEventClick} onSlotSelect={handleSlotSelect} />
        </div>

        <EventFicha
          event={selectedEvent}
          open={selectedEventId !== null}
          onOpenChange={(open) => {
            if (!open) closeFicha();
          }}
          onDelete={handleDeleteEvent}
        />

        <CreateEventDialog
          open={isCreateOpen}
          onOpenChange={setIsCreateOpen}
          defaultSlot={defaultSlot}
          onCreated={handleCreated}
        />

        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={(open) => {
            if (!open) handleCancel();
          }}
          title={confirmOptions?.title ?? ''}
          description={confirmOptions?.description}
          confirmLabel={confirmOptions?.confirmLabel}
          cancelLabel={confirmOptions?.cancelLabel}
          variant={confirmOptions?.variant}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      </div>
    </FeatureErrorBoundary>
  );
}
